'use client'

import { useState } from 'react'
import { cn } from '~shared/utils/cn'
import { LeadForm } from '~widgets/lead-form/components/lead-form'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'

// ─── Types ────────────────────────────────────────────────────────────────────

type TFAQProps = {
  program?: Awaited<ReturnType<typeof getProgramData>>
  isLeadForm?: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LABEL = 'Часто задаваемые вопросы'
const TITLE = 'Нас часто спрашивают'

const DEFAULT_QUESTIONS = [
  {
    question: 'Необходимо ли приезжать в ВУЗ?',
    answer:
      'Для обучения личного присутствия не требуется. Вся работа студентов от подачи документов для поступления до подготовки и защиты диплома проводится дистанционно'
  },
  {
    question: 'Могут ли поступить иностранные граждане?',
    answer:
      'Да, принимаем иностранных граждан в общем порядке. Нужно будет предоставить заверенные документы о вашем образовании'
  },
  {
    question: 'Можно ли оплатить обучение из другой страны?',
    answer:
      'Да, вы можете оплатить наш курс из любой точки мира. Рассрочка доступна только в России, но и для других регионов мы постараемся подобрать комфортный способ оплаты'
  },
  {
    question: 'Будет ли у меня наставник и техническая поддержка?',
    answer:
      'На протяжении всего обучения вас будет поддерживать куратор. Он поможет с любыми вопросами в процессе обучения'
  }
]

// ─── QuestionAndAnswer ────────────────────────────────────────────────────────

const QuestionAndAnswer = ({
  question,
  answer,
  isOpenDefault
}: {
  question: string | null
  answer: string | null
  isOpenDefault: boolean
}) => {
  const [isOpen, setIsOpen] = useState(isOpenDefault)

  if (!question || !answer) return null

  return (
    <div
      className='py-[16px] md:py-[24px] cursor-pointer'
      onClick={() => setIsOpen(prev => !prev)}>
      <div className='flex items-center justify-between gap-[16px]'>
        <h3 className='text-[16px] md:text-[18px] 2lg:text-[20px] font-medium leading-[120%]'>
          {question}
        </h3>
        {/* Rotating plus icon */}
        <div
          className={cn(
            'shrink-0 w-[24px] h-[24px] relative transition-transform duration-200',
            isOpen && 'rotate-45'
          )}>
          <span className='absolute top-1/2 left-0 w-full h-[2px] bg-white -translate-y-1/2' />
          <span className='absolute top-0 left-1/2 h-full w-[2px] bg-white -translate-x-1/2' />
        </div>
      </div>
      {isOpen && (
        <p className='text-[14px] md:text-[16px] leading-[150%] mt-[16px] text-white'>
          {answer}
        </p>
      )}
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FAQSection = ({ program, isLeadForm = true }: TFAQProps) => {
  const questions =
    program?.programFAQ && program.programFAQ.length > 0
      ? program.programFAQ
      : DEFAULT_QUESTIONS

  return (
    <section className='py-[56px] 2lg:py-[80px] bg-accent'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        {/* Header */}
        <div className='flex flex-col items-start mb-[32px] 2lg:mb-[48px] text-white'>
          <span className='inline-block mb-[16px] px-[16px] py-[8px] rounded-[55px] border border-white text-[12px] 2lg:text-[14px] font-medium leading-[120%]'>
            {LABEL}
          </span>
          <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-medium leading-[120%]'>
            {TITLE}
          </h2>
        </div>

        <div
          className={cn(
            'flex flex-col gap-[40px] 2lg:gap-[32px]',
            isLeadForm && '2lg:flex-row'
          )}>
          {/* Left: questions */}
          <div className='2lg:flex-[4] text-white'>
            {questions.map((item, idx) => (
              <div
                key={`FAQ__question--${idx + 1}`}
                className={cn(
                  'border-t border-white',
                  idx === questions.length - 1 && 'border-b'
                )}>
                <QuestionAndAnswer
                  question={item.question}
                  answer={item.answer}
                  isOpenDefault={idx === 0}
                />
              </div>
            ))}
          </div>

          {/* Right: lead form */}
          {isLeadForm && (
            <div className='2lg:flex-[3] p-[24px]  rounded-[16px] bg-[#F5F5F5] max-w-[510px]'>
              <p className='text-[20px] font-medium leading-[120%] mb-[24px] 2lg:mb-[32px] text-center'>
                Остались вопросы? Получите консультацию
                <br />
                приемной комиссии
              </p>
              <LeadForm
                formClassName='md:grid-cols-1'
                buttonText='Отправить заявку'
                program={{
                  productId: program?.productUid ?? null,
                  productName: program?.name ?? '',
                  productCategory: program?.category?.name ?? '',
                  productFaculty: program?.faculty?.name ?? ''
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
